/**
 * Module-level gate so non-hook code (keyboard handlers, word click,
 * audio wrapper) can ask "what's the current allowed max seek?" without
 * its own React subscription. `useChunkedExposure` publishes here; other
 * consumers read imperatively via `clampSeek` / `wouldCrossLock` or
 * reactively via `useSyncExternalStore(subscribeChunkedGate, getGateSnapshot)`.
 */

let _state = {
  enabled: false,
  allowedMaxSec: Infinity,
  locked: false,
  currentChunkIndex: 0,
  // Raw ratchet count; persisted so reload resumes the same cursor.
  maxUnlockedChunkIndex: 0,
  totalChunks: 1,
  chunks: [],
  audioDuration: 0,
};

const _subs = new Set();

const sameState = (a, b) =>
  a.enabled === b.enabled &&
  a.allowedMaxSec === b.allowedMaxSec &&
  a.locked === b.locked &&
  a.currentChunkIndex === b.currentChunkIndex &&
  a.maxUnlockedChunkIndex === b.maxUnlockedChunkIndex &&
  a.totalChunks === b.totalChunks &&
  a.audioDuration === b.audioDuration &&
  a.chunks === b.chunks;

export const publishChunkedGate = (state) => {
  const next = {
    enabled: !!state?.enabled,
    allowedMaxSec: state?.enabled ? state.lockBeyondSec : Infinity,
    locked: !!state?.locked,
    currentChunkIndex: state?.currentChunkIndex ?? 0,
    maxUnlockedChunkIndex: state?.maxUnlockedChunkIndex ?? 0,
    totalChunks: state?.totalChunks ?? 1,
    chunks: Array.isArray(state?.chunks) ? state.chunks : [],
    audioDuration: state?.audioDuration || 0,
  };
  // Skip fan-out when nothing subscribers care about has changed.
  // `chunks` is reference-compared; callers keep the same array ref when
  // the underlying data hasn't shifted.
  if (sameState(_state, next)) return;
  _state = next;
  _subs.forEach((fn) => fn());
};

if (typeof window !== "undefined") {
  window.__chunkedGate = () => _state;
}

export const subscribeChunkedGate = (fn) => {
  _subs.add(fn);
  return () => _subs.delete(fn);
};

export const getGateSnapshot = () => _state;

export const getAllowedMaxSec = () => _state.allowedMaxSec;

export const isGateActive = () => _state.enabled && _state.locked;

export const clampSeek = (sec) => {
  if (!isGateActive()) return sec;
  if (!Number.isFinite(_state.allowedMaxSec)) return sec;
  return Math.min(Math.max(0, sec), _state.allowedMaxSec);
};

export const wouldCrossLock = (sec) =>
  isGateActive() && Number.isFinite(_state.allowedMaxSec) && sec > _state.allowedMaxSec;
